import React, { useContext, useMemo, useState } from 'react'
import { Table, Button, Modal } from 'semantic-ui-react'
import ClientPageContext from './ClientPageContext'
import CaseType from '../../../../models/CaseType'
import ClientCaseTypeForm from './ClientCaseTypeForm'
import Client from '../../../../models/Client'
import LocalStorage from '../../../../lib/LocalStorage'
import { toast } from '../../../common/Toast'
import config from '../../../../common/repository/config'

interface Props {
    refreshTrigger?: number
    onUpdateSuccess?: () => void
}
const ClientCaseTypeList: React.FC<Props> = ({ refreshTrigger, onUpdateSuccess }) => {
    const ctx = useContext(ClientPageContext)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [selectedCaseType, setSelectedCaseType] = useState<CaseType | null>(null)
    const caseTypes = useMemo(() => {
        if (!ctx.client) return []
        return ctx.client.getCaseTypes()
    }, [ctx.client, refreshTrigger])
    const formatTAT = (hours: number): string => {
        const [days, hrs] = CaseType.getTATPair(hours)
        if (days === 0) return `${hrs} hours`
        return `${days} days, ${hrs} hours`
    }
    const handleOpenClick = (caseType: CaseType) => {
        setSelectedCaseType(caseType)
        setEditModalOpen(true)
    }
    const handleUpdateSuccess = (client: Client) => {
        ctx.setClient(client)
        setEditModalOpen(false)
        setSelectedCaseType(null)
        if (onUpdateSuccess) {
            onUpdateSuccess()
        }
    }
    if (!ctx.client) {
        return null
    }
  const enableClient = async (clientId: string | undefined,caseTypeId:string,isActive:boolean) => {
    if (!clientId) {
      toast.error("Client ID not found");
      return;
    }
    const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
    if (!token) {
      console.error("Authentication token is missing.");
      return;
    }
    try {
      const response = await fetch(`${config.apiBaseURL}/caseType/disable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({ clientId, caseTypeId, isActive }),
      });
      const data = await response.json();

      if (data.success) {
        // Trigger a reload of the client so case_types (with isActive) are remapped correctly
        ctx.setLoadClient(true);
        toast.success(data.message || "Status updated successfully!");
      } else {
        toast.error(data.message || "Something went wrong!");
      }
      console.log(data);
    } catch (error) {
      toast.error("Error occurred while fetching data");
    }
  };
    return (
        <>
            <div style={{ marginTop: '20px' }}>
                <Table celled striped compact>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Title</Table.HeaderCell>
                            <Table.HeaderCell>Client TAT</Table.HeaderCell>
                            <Table.HeaderCell>Internal TAT</Table.HeaderCell>
                            <Table.HeaderCell>Initial Portion Cap</Table.HeaderCell>
                            <Table.HeaderCell>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {caseTypes.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan="5" textAlign="center">
                                    No case types found
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            caseTypes.map((caseType) => {
                                const isActive = caseType.getActive();
                                const nextIsActive = !isActive; // desired state after click
                                return (
                                    <Table.Row key={caseType.getId()}>
                                        <Table.Cell>{caseType.getTitle()}</Table.Cell>
                                        <Table.Cell>{formatTAT(caseType.getInternalTAT())}</Table.Cell>
                                        <Table.Cell>{formatTAT(caseType.getInvestigatorTAT())}</Table.Cell>
                                        <Table.Cell>{caseType.getInitialCasePortionCapping()}</Table.Cell>
                                        <Table.Cell>
                                            <Button.Group size="mini">
                                                <Button onClick={() => handleOpenClick(caseType)}>Open</Button>
                                                <Button
                                                    color={isActive ? 'red' : 'green'}
                                                    onClick={() =>
                                                        enableClient(
                                                            ctx.client?.getId(),
                                                            caseType.getId(),
                                                            nextIsActive
                                                        )}
                                                >
                                                    {isActive ? 'Disable' : 'Enable'}
                                                </Button>
                                            </Button.Group>
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            })
                        )}
                    </Table.Body>
                </Table>
            </div>

            <Modal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false)
                    setSelectedCaseType(null)
                }}
                size="tiny"
                closeIcon
                closeOnDimmerClick={false}
                closeOnEscape={false}
            >
                <Modal.Header>Update Case Type</Modal.Header>
                <Modal.Content>
                    {selectedCaseType && (
                        <ClientCaseTypeForm
                            caseType={selectedCaseType}
                            onSuccess={handleUpdateSuccess}
                        />
                    )}
                </Modal.Content>
            </Modal>
        </>
    )
}

export default ClientCaseTypeList

